import type { ParsedField, UIJSONSchema7 } from "../types"

// ============================================================
// oneOf 조건부 스키마 파싱
// ============================================================

export interface OneOfCondition {
	discriminatorValue: string
	/** enum 기반 discriminator일 때 해당 조건의 enum 값 목록 */
	discriminatorEnumValues?: string[]
	requiredFields: string[]
}

export interface OneOfConditions {
	/** discriminator 필드명 (예: "category") */
	discriminatorField: string | null
	/** enum 기반 discriminator 여부 (const가 아닌 enum으로 판별) */
	isEnumBased?: boolean
	/** discriminator 값별 조건 목록 */
	conditions: OneOfCondition[]
	/** oneOf에서 사용되는 모든 조건부 필드명 */
	conditionalFields: Set<string>
}

/**
 * oneOf 스키마에서 discriminator 기반 조건 추출
 *
 * @example
 * // Schema with oneOf:
 * {
 *   "oneOf": [
 *     { "properties": { "category": { "const": "top" } }, "required": ["garment_image_url"] },
 *     { "properties": { "category": { "const": "bottom" } }, "required": ["garment_image_url"] }
 *   ]
 * }
 *
 * // Result:
 * {
 *   discriminatorField: "category",
 *   conditions: [
 *     { discriminatorValue: "top", requiredFields: ["garment_image_url"] },
 *     { discriminatorValue: "bottom", requiredFields: ["garment_image_url"] }
 *   ],
 *   conditionalFields: Set(["garment_image_url"])
 * }
 */
export function parseOneOfConditions(schema?: UIJSONSchema7): OneOfConditions {
	const result: OneOfConditions = {
		discriminatorField: null,
		conditions: [],
		conditionalFields: new Set()
	}

	if (!schema?.oneOf || !Array.isArray(schema.oneOf)) return result

	for (const condition of schema.oneOf) {
		if (typeof condition === "boolean") continue

		// discriminator 필드 찾기 (const 값을 가진 property)
		let discriminatorField: string | null = null
		let discriminatorValue: string | null = null

		if (condition.properties) {
			for (const [propName, propSchema] of Object.entries(condition.properties)) {
				if (typeof propSchema === "boolean") continue
				if (propSchema.const !== undefined) {
					discriminatorField = propName
					discriminatorValue = String(propSchema.const)
					break
				}
			}
		}

		if (!discriminatorField || !discriminatorValue) continue

		// discriminator 필드 일관성 확인 (첫 번째 발견된 것 사용)
		if (result.discriminatorField === null) {
			result.discriminatorField = discriminatorField
		} else if (result.discriminatorField !== discriminatorField) {
			// 다른 discriminator 필드가 발견되면 스킵
			continue
		}

		const requiredFields = condition.required || []
		result.conditions.push({ discriminatorValue, requiredFields })

		// 조건부 필드 수집
		for (const field of requiredFields) {
			result.conditionalFields.add(field)
		}
	}

	// const 기반 discriminator를 찾지 못했으면 enum 기반 감지 시도
	if (result.discriminatorField === null) {
		return detectEnumBasedDiscriminator(schema)
	}

	return result
}

/**
 * enum 기반 oneOf discriminator 감지
 *
 * 각 oneOf 조건의 enum 값이 서로 겹치지 않는(disjoint) 필드를 discriminator로 선정
 *
 * @example
 * // oneOf: [
 * //   { properties: { output_size: { enum: ["auto","landscape_16_9"] }, duration: { enum: [4,6,8] } } },
 * //   { properties: { output_size: { enum: ["landscape_16_9_1K"] }, duration: { enum: [8] } } }
 * // ]
 * // → discriminatorField: "output_size" (disjoint), duration은 dependent (overlapping)
 */
function detectEnumBasedDiscriminator(schema: UIJSONSchema7): OneOfConditions {
	const empty: OneOfConditions = { discriminatorField: null, conditions: [], conditionalFields: new Set() }
	if (!schema.oneOf || !Array.isArray(schema.oneOf)) return empty

	const conditions = schema.oneOf.filter((c): c is UIJSONSchema7 => typeof c !== "boolean" && !!c.properties)
	if (conditions.length < 2) return empty

	// 모든 조건에 enum으로 존재하는 공통 프로퍼티 수집
	const firstCondition = conditions[0]
	if (!firstCondition?.properties) return empty
	const firstConditionProps = firstCondition.properties
	const firstProps = Object.entries(firstConditionProps).filter(([, s]) => typeof s !== "boolean" && Array.isArray(s.enum))
	const commonEnumFields = firstProps
		.filter(([name]) =>
			conditions.every((c) => {
				const prop = c.properties?.[name]
				return prop && typeof prop !== "boolean" && Array.isArray(prop.enum)
			})
		)
		.map(([name]) => name)

	// disjoint 검사: 조건 간 enum 값이 겹치지 않는 필드 찾기
	let discriminatorField: string | null = null
	for (const fieldName of commonEnumFields) {
		const allValues = new Set<string>()
		let isDisjoint = true

		for (const condition of conditions) {
			const enumValues = (condition.properties![fieldName] as UIJSONSchema7).enum as Array<string | number>
			for (const v of enumValues) {
				const key = String(v)
				if (allValues.has(key)) {
					isDisjoint = false
					break
				}
				allValues.add(key)
			}
			if (!isDisjoint) break
		}

		if (isDisjoint) {
			discriminatorField = fieldName
			break
		}
	}

	if (!discriminatorField) return empty

	// 조건 구성
	const result: OneOfConditions = {
		discriminatorField,
		isEnumBased: true,
		conditions: [],
		conditionalFields: new Set()
	}

	for (const condition of conditions) {
		const enumValues = ((condition.properties![discriminatorField] as UIJSONSchema7).enum as Array<string | number>).map(String)
		if (enumValues.length === 0) continue // 빈 enum은 유효하지 않은 스키마이므로 skip
		const requiredFields = condition.required || []

		result.conditions.push({
			discriminatorValue: enumValues[0]!,
			discriminatorEnumValues: enumValues,
			requiredFields
		})

		for (const field of requiredFields) {
			// discriminator 필드 자체는 conditionalFields에서 제외 (항상 표시되어야 함)
			if (field !== discriminatorField) {
				result.conditionalFields.add(field)
			}
		}
	}

	return result
}

/**
 * 현재 discriminator 값에 따른 조건부 required 필드 목록 반환
 */
export function getConditionalRequiredFields(conditions?: OneOfConditions, discriminatorValue?: unknown): string[] {
	if (!conditions || typeof discriminatorValue !== "string") return []

	if (conditions.isEnumBased) {
		const condition = conditions.conditions.find((c) => c.discriminatorEnumValues?.includes(discriminatorValue))
		return condition?.requiredFields || []
	}

	const condition = conditions.conditions.find((c) => c.discriminatorValue === discriminatorValue)
	return condition?.requiredFields || []
}

/**
 * oneOf 조건에서 특정 discriminator 값에 해당하는 필드 오버라이드 반환
 *
 * const 기반 / enum 기반 discriminator 모두 지원
 *
 * @param schema - Root JSON Schema
 * @param discriminatorValue - 현재 discriminator 값
 * @param oneOfConditions - parseOneOfConditions 결과 (enum 기반 매칭에 필요)
 * @returns 필드명 → 오버라이드 스키마 맵
 */
function getOneOfFieldOverrides(schema: UIJSONSchema7, discriminatorValue?: unknown, oneOfConditions?: OneOfConditions): Record<string, UIJSONSchema7> {
	if (!schema.oneOf || typeof discriminatorValue !== "string") return {}

	// enum 기반 discriminator 처리
	if (oneOfConditions?.isEnumBased && oneOfConditions.discriminatorField) {
		const discriminatorField = oneOfConditions.discriminatorField

		for (const condition of schema.oneOf) {
			if (typeof condition === "boolean" || !condition.properties) continue

			const discProp = condition.properties[discriminatorField]
			if (!discProp || typeof discProp === "boolean" || !Array.isArray(discProp.enum)) continue

			// discriminator 필드의 enum에 현재 값이 포함되는 조건 찾기
			if (discProp.enum.map(String).includes(discriminatorValue)) {
				const overrides: Record<string, UIJSONSchema7> = {}
				for (const [fieldName, fieldSchema] of Object.entries(condition.properties)) {
					if (typeof fieldSchema === "boolean") continue
					if (fieldName === discriminatorField) continue // discriminator 필드 자체는 제외
					overrides[fieldName] = fieldSchema as UIJSONSchema7
				}
				return overrides
			}
		}

		return {}
	}

	// const 기반 discriminator 처리 (기존 로직)
	for (const condition of schema.oneOf) {
		if (typeof condition === "boolean" || !condition.properties) continue

		for (const [, propSchema] of Object.entries(condition.properties)) {
			if (typeof propSchema === "boolean") continue

			if (propSchema.const === discriminatorValue) {
				const overrides: Record<string, UIJSONSchema7> = {}
				for (const [fieldName, fieldSchema] of Object.entries(condition.properties)) {
					if (typeof fieldSchema === "boolean") continue
					if (fieldSchema.const !== undefined) continue // discriminator 필드 제외
					overrides[fieldName] = fieldSchema as UIJSONSchema7
				}
				return overrides
			}
		}
	}

	return {}
}

function shouldSkipField(name: string, deprecatedFields: string[]): boolean {
	return deprecatedFields.includes(name)
}

export interface ParseSchemaOptions {
	deprecatedFields?: string[]
	oneOfConditions?: OneOfConditions
	discriminatorValue?: unknown
}

/**
 * oneOf 조건을 포함한 JSON Schema 파싱
 *
 * discriminatorValue가 제공되면 해당 oneOf 조건의 필드 오버라이드(minItems, maxItems 등)를
 * 필드 스키마에 병합합니다.
 *
 * @param schema - JSON Schema
 * @param options - 파싱 옵션
 * @returns ParsedField 배열
 *
 * @example
 * const conditions = parseOneOfConditions(schema)
 * const fields = parseSchema(schema, { deprecatedFields: ["num_images"] })
 * const fields = parseSchema(schema, { oneOfConditions: conditions, discriminatorValue: "top" })
 */
export function parseSchema(schema?: UIJSONSchema7, options?: ParseSchemaOptions): ParsedField[] {
	if (!schema || schema.type !== "object" || !schema.properties) return []

	const { deprecatedFields = [], oneOfConditions, discriminatorValue } = options ?? {}

	// discriminator 값으로 조건부 required 필드 계산
	const conditionalRequired = getConditionalRequiredFields(oneOfConditions, discriminatorValue)

	// 기본 required + 조건부 required 병합
	const baseRequired = schema.required || []
	const allRequired = [...new Set([...baseRequired, ...conditionalRequired])]

	// 조건부 필드 Set
	const conditionalFields = oneOfConditions?.conditionalFields ?? new Set<string>()

	// oneOf 필드 오버라이드 (minItems, maxItems, enum 등)
	const fieldOverrides = getOneOfFieldOverrides(schema, discriminatorValue, oneOfConditions)

	const fields: ParsedField[] = []

	for (const [name, fieldSchema] of Object.entries(schema.properties)) {
		if (shouldSkipField(name, deprecatedFields)) continue

		// 조건부 필드인 경우: 현재 조건에 required가 아니면 숨김
		if (conditionalFields.has(name) && !conditionalRequired.includes(name)) {
			continue
		}

		// oneOf 오버라이드가 있으면 병합
		const mergedSchema = fieldOverrides[name] ? { ...fieldSchema, ...fieldOverrides[name] } : fieldSchema

		fields.push({
			name,
			schema: mergedSchema,
			required: allRequired.includes(name)
		})
	}

	return fields
}

export function getDefaultValues(schema: UIJSONSchema7 | undefined): Record<string, unknown> {
	if (!schema || schema.type !== "object" || !schema.properties) return {}

	const defaults: Record<string, unknown> = {}

	const requiredFields = new Set(schema.required ?? [])

	for (const [name, fieldSchema] of Object.entries(schema.properties)) {
		if (fieldSchema.default !== undefined) {
			defaults[name] = fieldSchema.default
		} else if (fieldSchema.type === "array") {
			// array 타입은 undefined 방지를 위해 빈 배열로 초기화
			defaults[name] = []
		} else if (requiredFields.has(name) && fieldSchema.enum && fieldSchema.enum.length > 0) {
			// required enum 필드에 default가 없으면 첫 번째 값을 기본값으로 설정
			// EnumField UI에서 보여주는 값과 폼 상태를 일치시키기 위함
			defaults[name] = fieldSchema.enum[0]
		}
	}

	return defaults
}

export function isValidSchema(schema: UIJSONSchema7): boolean {
	if (!schema) return false
	if (schema.type !== "object") return false
	if (!schema.properties) return false

	return true
}
